import { User } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

export interface PersistedUserData {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  providerId: string;
  metadata: {
    creationTime: string | undefined;
    lastSignInTime: string | undefined;
  };
}

export const persistUserData = async (user: User): Promise<void> => {
  const userData: PersistedUserData = {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
    providerId: user.providerId,
    metadata: {
      creationTime: user.metadata.creationTime,
      lastSignInTime: user.metadata.lastSignInTime
    }
  };

  try {
    // Récupérer d'abord les données existantes de Firestore
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const existingData = userDoc.data();
      // Conserver l'URL de la photo existante si elle existe
      if (existingData.photoURL) {
        userData.photoURL = existingData.photoURL;
      }
    }

    // Sauvegarder dans localStorage
    localStorage.setItem('userData', JSON.stringify(userData));

    // Sauvegarder dans Firestore
    await setDoc(userRef, {
      ...userData,
      updatedAt: new Date().toISOString()
    }, { merge: true });
  } catch (error) {
    console.error('Error persisting user data:', error);
  }
};

export const getPersistedUserData = async (userId: string): Promise<PersistedUserData | null> => {
  try {
    // Récupérer depuis Firestore en priorité
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const data = userDoc.data();
      const userData: PersistedUserData = {
        uid: userId,
        email: data.email,
        displayName: data.displayName,
        photoURL: data.photoURL,
        emailVerified: data.emailVerified,
        providerId: data.providerId,
        metadata: data.metadata || {}
      };
      
      // Mettre à jour localStorage avec les dernières données
      localStorage.setItem('userData', JSON.stringify(userData));
      return userData;
    }

    // Fallback vers localStorage
    const localData = localStorage.getItem('userData');
    if (localData) {
      const parsedData = JSON.parse(localData);
      // Vérifier que les données correspondent à l'utilisateur actuel
      if (parsedData.uid === userId) {
        return parsedData;
      }
    }
    return null;
  } catch (error) {
    console.error('Error retrieving persisted user data:', error);
    return null;
  }
};

export const updatePersistedUserData = async (userId: string, updates: Partial<PersistedUserData>): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    
    // Récupérer les données existantes avant la mise à jour
    const userDoc = await getDoc(userRef);
    const existingData = userDoc.exists() ? userDoc.data() : {};
    
    // Si une mise à jour de photoURL est demandée, vérifier qu'elle est différente
    if (updates.photoURL !== undefined && updates.photoURL === existingData.photoURL) {
      delete updates.photoURL;
    }

    if (Object.keys(updates).length > 0) {
      // Mettre à jour Firestore
      await setDoc(userRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      // Mettre à jour localStorage
      const localData = localStorage.getItem('userData');
      if (localData) {
        const currentData = JSON.parse(localData);
        const updatedData = {
          ...currentData,
          ...updates,
        };
        localStorage.setItem('userData', JSON.stringify(updatedData));
      }
    }
  } catch (error) {
    console.error('Error updating persisted user data:', error);
  }
};

export const clearPersistedUserData = (): void => {
  localStorage.removeItem('userData');
};